import React, { useEffect, useRef, useState } from "react";
import { graphql } from "gatsby";
import { motion } from "framer-motion";
import { FADE_UP, useInView } from "../animationUtils";
import htmlLinkResolver from "../../utils/htmlLinkResolver";

export const fragments = graphql`
  fragment Page_CapabilitiesAccordion on PrismicPageDataBodyCapabilitiesAccordion {
    primary {
      capsule_text1
      capsule_url {
        url
      }
      title1 {
        text
      }
      body2 {
        richText
        html
      }
    }
    items {
      accordion_item_title {
        text
      }
      accordion_item_body {
        richText
        html
      }
    }
  }
  fragment FocusArea_CapabilitiesAccordion on PrismicFocusAreaDataBodyCapabilitiesAccordion {
    primary {
      capsule_text1
      capsule_url {
        url
      }
      title1 {
        text
      }
      body2 {
        richText
        html
      }
    }
    items {
      accordion_item_title {
        text
      }
      accordion_item_body {
        richText
        html
      }
    }
  }
  fragment Project_CapabilitiesAccordion on PrismicProjectDataBodyCapabilitiesAccordion {
    primary {
      capsule_text1
      capsule_url {
        url
      }
      title1 {
        text
      }
      body2 {
        richText
        html
      }
    }
    items {
      accordion_item_title {
        text
      }
      accordion_item_body {
        richText
        html
      }
    }
  }
  fragment Homepage_CapabilitiesAccordion on PrismicHomepageDataBodyCapabilitiesAccordion {
    primary {
      capsule_text1
      capsule_url {
        url
      }
      title1 {
        text
      }
      body2 {
        richText
        html
      }
    }
    items {
      accordion_item_title {
        text
      }
      accordion_item_body {
        richText
        html
      }
    }
  }
`;

function CapabilitiesAccordion({ data }) {
  const [refImage, inViewImage] = useInView();
  const [openItems, setOpenItems] = useState([]);
  const [accordionHeight, setAccordionHeight] = useState(0);
  const [isDesktop, setIsDesktop] = useState(false);
  const refAccordion = useRef(null);

  useEffect(() => {
    const handleResize = () => {
      setIsDesktop(window.innerWidth >= 992);
      setAccordionHeight(refAccordion.current?.clientHeight);
    };

    handleResize();
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  const toggleItem = (index) => {
    setOpenItems(prev => {
      if (prev.includes(index)) {
        return prev.filter(i => i !== index);
      } else {
        return [index];
      }
    });
  };

  return (
    <div className="capabilities-accordion">
      <motion.div
        ref={refImage}
        {...FADE_UP}
        animate={inViewImage ? "start" : ""}
        className="capabilities-accordion__content"
      >
        <div className="capabilities-accordion__content-header" style={isDesktop ? { height: accordionHeight } : {}}>
          <div className="capabilities-accordion__content-header-text">
            {
              data?.primary?.capsule_text1 && data?.primary?.capsule_url?.url && (
                <a href={data?.primary?.capsule_url?.url} className="capabilities-accordion__button">
                  {data?.primary?.capsule_text1}
                </a>
              )
            }
            {
              data?.primary?.capsule_text1 && !data?.primary?.capsule_url?.url && (
                <span className="capabilities-accordion__button">
                  {data?.primary?.capsule_text1}
                </span>
              )
            }
            {
              data?.primary?.title1?.text && (
                <h3 className="capabilities-accordion__headline">{data?.primary?.title1?.text}</h3>
              )
            }
            {
              data?.primary?.body2?.html?.length > 0 &&
              <div className="capabilities-accordion__body" dangerouslySetInnerHTML={{ __html: htmlLinkResolver(data?.primary?.body2?.html) }} />
            }
          </div>
        </div>
        <div className="capabilities-accordion__content-accordion" ref={refAccordion}>
          {
            data?.items?.map((item, index) => (
              <div key={index} className="capabilities-accordion__accordion-item-wrapper">
                <div
                  className={`capabilities-accordion__accordion-item ${openItems.includes(index) ? 'active' : ''}`}
                  onClick={() => toggleItem(index)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      toggleItem(index);
                    }
                  }}
                  role="button"
                  style={{ cursor: 'pointer' }}
                  tabIndex={0} /* eslint-disable-line jsx-a11y/no-noninteractive-tabindex */
                >
                  <h4 className="capabilities-accordion__accordion-item-title">
                    {item?.accordion_item_title?.text}
                    {
                      openItems.includes(index) ? (
                        <svg width="42" height="42" fill="none">
                          <circle cx="21" cy="21" r="20" stroke="#50B848" transform="rotate(90 21 21)" />
                          <path fill="#50B848" fillRule="evenodd" d="M29 21.016v1L13.959 22v-1L29 21.016Z" clipRule="evenodd" />
                        </svg>
                      ) : (
                        <svg width="42" height="42" fill="none">
                          <circle cx="21" cy="21" r="20" stroke="#002B41" transform="rotate(90 21 21)"/>
                          <path fill="#002B41" fillRule="evenodd" d="M29 21.016v1L13.959 22v-1L29 21.016Z" clipRule="evenodd"/>
                          <path fill="#002B41" fillRule="evenodd" d="M21.973 29.03h-1l.015-15.042h1l-.015 15.041Z" clipRule="evenodd"/>
                        </svg>
                      )
                    }
                  </h4>
                </div>
                {
                  item?.accordion_item_body?.html &&
                  <motion.div
                    initial={false}
                    animate={{
                      height: openItems.includes(index) ? "auto" : 0,
                      opacity: openItems.includes(index) ? 1 : 0,
                      paddingBottom: openItems.includes(index) ? '1.5rem' : 0
                    }}
                    transition={{
                      duration: 0.3,
                      ease: "easeInOut",
                      paddingBottom: { duration: 0.5, ease: "easeIn" }
                    }}
                    style={{ overflow: "hidden" }}
                    className="capabilities-accordion__accordion-item-body"
                  >
                    <div  dangerouslySetInnerHTML={{ __html: htmlLinkResolver(item?.accordion_item_body?.html) }} />
                  </motion.div>
                }
              </div>
            ))
          }
        </div>

      </motion.div>

    </div>
  );
}

export default CapabilitiesAccordion;
